/**
 * Iter 78 · Backward-compatible readers for `meeting_structured.*` array
 * entries. Legacy notes (pre-Iter-74) stored `decisions`, `follow_ups`,
 * `open_questions`, `key_points` as arrays of plain strings. Iter-74's
 * Note-scoped Briefing Studio Apply started writing dicts to preserve
 * provenance/idempotency metadata (`source_proposal_id`, `created_by`,
 * `created_at`, and the actual content field varies by category).
 *
 * Every renderer that touches these arrays MUST tolerate both shapes.
 * React crashes hard if you try to render a raw object as a child —
 * that's the P0 regression this module exists to prevent.
 *
 * DO NOT rewrite historical string-shaped rows to dict shape just to
 * make the UI simpler. Keep the compatibility rule; migrating touches
 * evidentiary history we don't own.
 */

/**
 * Return the primary display text for an item that may be a legacy
 * string OR a dict of shape `{text, source_proposal_id, …}`.
 *
 * Passes through `null` / `undefined` unchanged so callers can render
 * "(none)" placeholders themselves.
 */
export const entryText = (item) => {
  if (item == null) return item;
  if (typeof item === "string") return item;
  if (typeof item !== "object") return String(item);
  // Iter 74 dicts store the copy under `text` for decisions/follow_ups/
  // open_questions. Fall back to `title`/`body` for older or supporting-
  // note shaped rows so we never bottom out at `[object Object]`.
  return item.text ?? item.title ?? item.body ?? "";
};

/**
 * Extract the multi-line body for a Key Point / Supporting Observation
 * entry. Legacy notes stored raw strings; Iter-74 stores
 * `{title, body, source_proposal_id, …}`. Returns `{title, body}` so
 * renderers can decide their own layout.
 */
export const keyPointParts = (item) => {
  if (item == null) return { title: null, body: "" };
  if (typeof item === "string") return { title: null, body: item };
  if (typeof item !== "object") return { title: null, body: String(item) };
  return { title: item.title || null, body: item.body || item.text || "" };
};
